import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Award, Clock, Copy, ExternalLink, GraduationCap } from 'lucide-react'
import { botonExamen, cursoPendiente, tiempoRestante, textoReloj, leerRespuestas, guardarRespuestas, borrarRespuestas } from '@/lib/examenIntento'
import toast from 'react-hot-toast'
import cd2bCertificacionService from '@/services/cd2bCertificacionService'
import { useAuthStore } from '@/store/authStore'
import config from '@/config'
import { urlVerificacion } from '@/lib/urlCredencial'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'

const NIVEL_LABEL = { aprendiz: 'Aprendiz', certificado: 'Certificado', especialista: 'Especialista', maestro: 'Maestro' }
const TRACK_LABEL = { ventas: 'Ventas', implementacion: 'Implementación', fiscal_sv: 'Fiscal El Salvador', soporte: 'Soporte' }
const ESTADO_INSIGNIA = {
  vigente: { label: 'Vigente', variant: 'success' },
  por_vencer: { label: 'Por vencer', variant: 'warning' },
  vencida: { label: 'Vencida', variant: 'danger' },
  revocada: { label: 'Revocada', variant: 'danger' },
}

/**
 * Mi certificación (docs/120 §7.5): insignias por track + exámenes disponibles + credencial
 * pública. "Certificamos a la persona, no a la empresa" — cada track es independiente (§5.1).
 */
export default function ReventaCertificacion() {
  const navigate = useNavigate()
  const partner = useAuthStore((s) => s.partner)
  const [cargando, setCargando] = useState(true)
  const [insignias, setInsignias] = useState([])
  const [examenes, setExamenes] = useState([])
  const [intento, setIntento] = useState(null) // { examen, intentoId, minutos, expiraAt, preguntas }
  const [respuestas, setRespuestas] = useState({}) // preguntaId -> [posiciones]
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)
  const [porConfirmar, setPorConfirmar] = useState(null) // examen que pide confirmación antes de gastar un intento
  const [entiendo, setEntiendo] = useState(false)
  const [ahora, setAhora] = useState(() => Date.now())
  const autoEntregado = useRef(false)

  // `silencioso`: recarga sin esqueleto (si no, el diálogo del examen desaparece mientras carga).
  const cargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!silencioso) setCargando(true)
    try {
      const [ins, ex] = await Promise.all([
        cd2bCertificacionService.misInsignias(),
        cd2bCertificacionService.examenes(),
      ])
      setInsignias(ins?.data?.insignias || [])
      setExamenes(ex?.data?.examenes || [])
    } catch {
      toast.error('No se pudo cargar tu certificación')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const insigniaVigente = insignias.find((i) => i.track === 'implementacion' && (i.estado === 'vigente' || i.estado === 'por_vencer'))
    || insignias[0] || null

  // Abre un intento nuevo o RETOMA el que quedó abierto (el servidor decide: mismo intento, mismo reloj).
  const comenzarExamen = async (examen) => {
    setPorConfirmar(null)
    setEntiendo(false)
    try {
      const res = await cd2bCertificacionService.iniciarIntento(examen.codigo)
      const datos = res.data || {}
      autoEntregado.current = false
      setIntento({ examen, ...datos })
      setRespuestas(datos.retomado ? leerRespuestas(datos.intentoId) : {})
      setResultado(null)
      setAhora(Date.now())
      if (datos.retomado) toast('Retomaste tu examen. El tiempo siguió corriendo mientras estabas fuera.', { icon: '⏱️' })
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo iniciar el examen')
      cargar({ silencioso: true })
    }
  }

  // Lo marcado se guarda en este navegador: si cierra por accidente y retoma, no lo pierde.
  useEffect(() => {
    if (intento?.intentoId && !resultado) guardarRespuestas(intento.intentoId, respuestas)
  }, [intento, respuestas, resultado])

  // Reloj del examen abierto.
  useEffect(() => {
    if (!intento || resultado) return undefined
    const t = setInterval(() => setAhora(Date.now()), 1000)
    return () => clearInterval(t)
  }, [intento, resultado])

  const restanteMs = intento && !resultado ? tiempoRestante(intento.expiraAt, ahora) : 0

  const marcarRespuesta = (preguntaId, posicion, tipo) => {
    setRespuestas((prev) => {
      if (tipo === 'multiple') {
        const actual = prev[preguntaId] || []
        const siguiente = actual.includes(posicion) ? actual.filter((p) => p !== posicion) : [...actual, posicion]
        return { ...prev, [preguntaId]: siguiente }
      }
      return { ...prev, [preguntaId]: [posicion] }
    })
  }

  const entregar = async () => {
    if (!intento) return
    const payload = Object.entries(respuestas).map(([preguntaId, seleccion]) => ({ preguntaId, seleccion }))
    setEnviando(true)
    try {
      const res = await cd2bCertificacionService.entregarIntento(intento.intentoId, payload)
      borrarRespuestas(intento.intentoId)
      setResultado(res.data)
      if (res.data?.aprobado) {
        toast.success('¡Aprobaste! Tu insignia ya está activa.')
      } else {
        toast('No alcanzaste el umbral esta vez — revisa el detalle por área.', { icon: '📋' })
      }
      cargar({ silencioso: true })
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo entregar el examen')
    } finally {
      setEnviando(false)
    }
  }

  // A 5 segundos del final se entrega solo lo que lleve marcado: después el servidor ya no lo califica.
  useEffect(() => {
    if (intento && !resultado && !enviando && !autoEntregado.current && restanteMs > 0 && restanteMs <= 5000) {
      autoEntregado.current = true
      toast('Se acabó el tiempo: entregamos lo que marcaste.', { icon: '⏱️' })
      entregar()
    }
  }, [intento, resultado, enviando, restanteMs]) // eslint-disable-line react-hooks/exhaustive-deps

  // Al salir sin entregar, la lista se recarga para que el botón diga «Continuar examen».
  const cerrarExamen = () => { setIntento(null); setResultado(null); setRespuestas({}); cargar({ silencioso: true }) }

  const copiarCredencial = () => {
    const url = urlVerificacion(insigniaVigente?.credencialPublica?.slug, config.apiUrl)
    if (!url) return toast.error('No pudimos armar el enlace de tu credencial en este entorno.')
    navigator.clipboard?.writeText(url)
    toast.success('Enlace copiado')
  }

  const totalPreguntas = intento?.preguntas?.length || 0
  const respondidas = useMemo(() => Object.values(respuestas).filter((s) => s.length > 0).length, [respuestas])

  if (cargando) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Mi certificación"
        description={partner?.organizationType === 'cd2b_consultant'
          ? 'Certificamos a la persona, no a la empresa — cada track tiene su propio examen y su vigencia.'
          : 'Certificamos a la persona, no a la empresa — cada track tiene su propio examen, vigencia y tope de margen.'}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        {insignias.length === 0 ? (
          <Card className="sm:col-span-2">
            <CardContent className="p-6 text-center text-body-sm text-neutral-500 dark:text-neutral-400">
              Todavía no tienes ninguna insignia. Presenta tu primer examen abajo para desbloquear la venta a clientes reales.
            </CardContent>
          </Card>
        ) : insignias.map((i) => {
          const est = ESTADO_INSIGNIA[i.estado] || { label: i.estado, variant: 'secondary' }
          const diasRestantes = i.expiraAt ? Math.ceil((new Date(i.expiraAt) - Date.now()) / 86400000) : null
          return (
            <Card key={i._id} className="p-5 flex gap-4 items-start">
              <div className="h-14 w-14 rounded-full flex-none flex items-center justify-center font-display text-h2 font-semibold"
                style={{ background: 'radial-gradient(circle at 32% 28%, #f3d99a, #B8924A 62%, #7A5E29)', color: '#2a1d08' }}>
                {(NIVEL_LABEL[i.nivel] || i.nivel)?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">{NIVEL_LABEL[i.nivel] || i.nivel} · {TRACK_LABEL[i.track] || i.track}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-1.5 text-caption text-neutral-600 dark:text-neutral-400">
                  <Badge variant={est.variant}>{est.label}</Badge>
                  {diasRestantes != null && <span>{diasRestantes > 0 ? `Vence en ${diasRestantes} días` : 'Vencida'}</span>}
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      <div className="space-y-3">
        <div>
          <h2 className="text-h3 font-display font-semibold text-neutral-900 dark:text-neutral-50">Exámenes disponibles</h2>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400">Cada vez que abres un examen usas uno de tus intentos, y no se recuperan. Ábrelo solo cuando hayas estudiado el curso y la guía de estudio.</p>
        </div>
        <Card>
          <CardContent className="p-0 divide-y divide-neutral-100 dark:divide-neutral-800">
            {examenes.length === 0 && (
              <p className="p-5 text-body-sm text-neutral-500 dark:text-neutral-400">No hay exámenes activos en este momento.</p>
            )}
            {examenes.map((ex) => {
              const bloqueadoPorPrereq = !ex.prerequisitos?.cumple
              const enfriando = ex.enfriandoHasta && new Date(ex.enfriandoHasta) > new Date()
              const boton = botonExamen(ex, ahora)
              const abiertoMs = boton.accion === 'retomar' ? tiempoRestante(ex.intentos?.enCursoExpiraAt, ahora) : 0
              return (
                <div key={ex.codigo} className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-900 dark:text-neutral-50">{ex.codigo} · {TRACK_LABEL[ex.track] || ex.track} — {NIVEL_LABEL[ex.nivel] || ex.nivel}</p>
                    <p className="text-caption text-neutral-500 dark:text-neutral-400 mt-0.5">
                      {ex.preguntasPorIntento} preguntas · {ex.minutos} min · aprueba con {Math.round((ex.umbralAprobacion || 0) * 100)}%
                      {' · '}{ex.intentos?.restantes ?? 0} intentos disponibles
                    </p>
                    {bloqueadoPorPrereq && (
                      <p className="text-caption text-warning-700 dark:text-warning-300 mt-0.5">Requiere antes: {(ex.prerequisitos?.faltantes || []).join(', ')}</p>
                    )}
                    {boton.accion === 'retomar' && (
                      <p className="text-caption text-warning-700 dark:text-warning-300 mt-0.5 flex items-center gap-1">
                        <Clock size={12} /> Tienes este examen abierto: te quedan {Math.max(1, Math.ceil(abiertoMs / 60000))} min para entregarlo.
                      </p>
                    )}
                    {enfriando && (
                      <p className="text-caption text-warning-700 dark:text-warning-300 mt-0.5 flex items-center gap-1"><Clock size={12} /> Disponible de nuevo el {new Date(ex.enfriandoHasta).toLocaleString('es-SV')}</p>
                    )}
                    {cursoPendiente(ex) && (
                      <p className="text-caption text-warning-700 dark:text-warning-300 mt-0.5">
                        El examen se abre cuando terminas el curso: marca como hechas todas sus lecciones.
                      </p>
                    )}
                    {ex.cursoRecomendado && (
                      <button
                        type="button"
                        onClick={() => navigate('/reventa/aprendizaje')}
                        className="mt-1 inline-flex min-h-9 items-center gap-1.5 text-caption text-primary hover:underline dark:text-primary-300"
                      >
                        <GraduationCap size={13} />
                        {ex.requiereCursoCompletado ? 'Curso del examen' : 'Curso recomendado'} {ex.cursoRecomendado.progresoPct != null ? `· ${ex.cursoRecomendado.progresoPct}% completado` : ''}
                      </button>
                    )}
                  </div>
                  <Button
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={!boton.accion}
                    onClick={() => (boton.accion === 'retomar' ? comenzarExamen(ex) : (setEntiendo(false), setPorConfirmar(ex)))}
                  >
                    {boton.texto}
                  </Button>
                </div>
              )
            })}
          </CardContent>
        </Card>
      </div>

      {insigniaVigente?.credencialPublica?.slug && (
        <div className="space-y-3">
          <div>
            <h2 className="text-h3 font-display font-semibold text-neutral-900 dark:text-neutral-50">Tu credencial pública</h2>
            <p className="text-body-sm text-neutral-500 dark:text-neutral-400">Un enlace verificable que puedes compartir con tus clientes.</p>
          </div>
          <Card className="flex flex-wrap items-center gap-3 p-4 sm:flex-nowrap sm:gap-4">
            <div className="h-12 w-12 rounded-md bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-400 dark:text-neutral-500 flex-none">
              <Award size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-neutral-900 dark:text-neutral-50 text-body-sm">
                {[partner?.organizationName].filter(Boolean).join(' · ') || 'Tu credencial'}
              </p>
              <p className="break-all font-mono text-caption text-primary dark:text-primary-300">
                {(urlVerificacion(insigniaVigente.credencialPublica.slug, config.apiUrl) || '').replace('https://', '') || `/certificacion/verificar/${insigniaVigente.credencialPublica.slug}`}
              </p>
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={copiarCredencial}><Copy size={14} /> Copiar enlace</Button>
              <Button asChild variant="ghost" size="icon-sm" className="h-10 w-10">
                <a href={urlVerificacion(insigniaVigente.credencialPublica.slug, config.apiUrl) || undefined} target="_blank" rel="noreferrer" aria-label="Abrir tu credencial pública">
                  <ExternalLink size={15} />
                </a>
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ── Modal: confirmar antes de gastar un intento ── */}
      <Dialog open={!!porConfirmar} onOpenChange={(open) => { if (!open) { setPorConfirmar(null); setEntiendo(false) } }}>
        <DialogContent className="max-w-lg">
          {porConfirmar && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-warning-600" /> Antes de abrir el examen</DialogTitle>
                <DialogDescription>
                  {porConfirmar.codigo} · {porConfirmar.preguntasPorIntento} preguntas · {porConfirmar.minutos} minutos · apruebas con {Math.round((porConfirmar.umbralAprobacion || 0) * 100)}%
                </DialogDescription>
              </DialogHeader>
              <ul className="space-y-2 text-body-sm text-foreground list-disc pl-5">
                <li>
                  <strong>Al tocar «Abrir el examen» usas uno de tus intentos</strong> (te quedan {porConfirmar.intentos?.restantes ?? 0} de {porConfirmar.intentosMax}). Los intentos no se recuperan.
                </li>
                <li>
                  Desde ese momento tienes <strong>{porConfirmar.minutos} minutos</strong>. El tiempo sigue corriendo aunque cierres la ventana. Si se te cierra, vuelve aquí y toca «Continuar examen» antes de que se acabe.
                </li>
                <li>Si no lo entregas a tiempo, pierdes ese intento sin calificación.</li>
                {porConfirmar.esperaEntreIntentosHoras > 0 && (
                  <li>Entre un intento y otro hay que esperar {porConfirmar.esperaEntreIntentosHoras} horas.</li>
                )}
              </ul>
              <div className="rounded-md border border-line bg-neutral-50 dark:bg-neutral-900 p-3 text-body-sm text-foreground">
                <p className="font-medium">Ábrelo solo cuando estés seguro.</p>
                <p className="mt-1 text-neutral-600 dark:text-neutral-400">
                  Antes, estudia todo el curso y la guía de estudio del examen en «Mi aprendizaje»
                  {porConfirmar.cursoRecomendado?.progresoPct != null ? ` (llevas ${porConfirmar.cursoRecomendado.progresoPct}% del curso)` : ''}.
                </p>
              </div>
              <label className="flex min-h-10 cursor-pointer items-center gap-2.5 text-body-sm text-foreground">
                <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary" checked={entiendo} onChange={(e) => setEntiendo(e.target.checked)} />
                Entiendo que al abrirlo uso un intento.
              </label>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => { setPorConfirmar(null); navigate('/reventa/aprendizaje') }}>
                  <GraduationCap size={14} /> Todavía no, voy a estudiar
                </Button>
                <Button disabled={!entiendo} onClick={() => comenzarExamen(porConfirmar)}>Abrir el examen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modal: presentar examen ── */}
      {/* Un clic afuera o Esc no lo cierran: se sale solo con «Salir sin entregar» (el reloj sigue). */}
      <Dialog open={!!intento} onOpenChange={(open) => { if (!open) cerrarExamen() }}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          {intento && !resultado && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pr-6">
                  <span>{intento.examen?.codigo}</span>
                  <span className={`inline-flex items-center gap-1 font-mono text-body-sm ${restanteMs <= 5 * 60000 ? 'text-danger-600 dark:text-danger-400' : 'text-neutral-600 dark:text-neutral-300'}`}>
                    <Clock size={14} /> {textoReloj(restanteMs)}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  {totalPreguntas} preguntas · respondidas {respondidas}/{totalPreguntas} · al llegar a 00:00 se entrega solo lo que hayas marcado
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
                {(intento.preguntas || []).map((p, idx) => (
                  <div key={p.preguntaId} className="border-b border-neutral-100 dark:border-neutral-800 pb-4 last:border-0">
                    <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50 mb-2">{idx + 1}. {p.texto}</p>
                    <div className="space-y-1.5">
                      {(p.opciones || []).map((op, pos) => {
                        const marcada = (respuestas[p.preguntaId] || []).includes(pos)
                        return (
                          <label key={pos} className={`flex min-h-10 cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-body-sm text-foreground ${marcada ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/30 dark:border-primary-600' : 'border-line'}`}>
                            <input
                              type={p.tipo === 'multiple' ? 'checkbox' : 'radio'}
                              name={p.preguntaId}
                              className="h-4 w-4 shrink-0 accent-primary"
                              checked={marcada}
                              onChange={() => marcarRespuesta(p.preguntaId, pos, p.tipo)}
                            />
                            {op}
                          </label>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-caption text-neutral-500 dark:text-neutral-400">
                Si sales sin entregar, el tiempo sigue corriendo. Puedes volver con «Continuar examen» mientras quede tiempo.
              </p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={cerrarExamen}>Salir sin entregar</Button>
                <Button onClick={entregar} disabled={enviando}>{enviando ? 'Entregando…' : 'Entregar examen'}</Button>
              </DialogFooter>
            </>
          )}

          {resultado && (
            <>
              <DialogHeader>
                <DialogTitle>{resultado.aprobado ? '¡Aprobaste!' : 'No alcanzaste el umbral'}</DialogTitle>
                <DialogDescription>
                  Puntaje: {Math.round(resultado.puntaje * 100)}% · mínimo {Math.round(resultado.umbralAprobacion * 100)}%
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {(resultado.porArea || []).map((a) => (
                  <div key={a.area} className="flex items-center justify-between text-body-sm">
                    <span className="text-neutral-600 dark:text-neutral-400">{a.area}</span>
                    <span className="font-mono text-foreground">{a.correctas}/{a.total}</span>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={cerrarExamen}>Cerrar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
